import type { OrderHistoryEntry } from '../types';

export type RefundStatus = 'submitted' | 'refunded' | 'rejected' | 'cancelled';

const PENDING_STATUSES = new Set([
  'submitted',
  'refund_submitted',
  'refund_pending',
  'cancel_submitted',
]);

function normalize(value?: string | null): RefundStatus | null {
  const status = String(value || '').trim().toLowerCase();
  if (status === 'refunded') return 'refunded';
  if (status === 'rejected' || status === 'refund_rejected') return 'rejected';
  if (status === 'cancelled') return 'cancelled';
  if (PENDING_STATUSES.has(status)) return 'submitted';
  return null;
}

export function isTerminalRefundStatus(status: RefundStatus | null): boolean {
  return status === 'refunded' || status === 'rejected' || status === 'cancelled';
}

/**
 * Terminal states always beat a stale submitted value. The dedicated
 * refund_status field wins when both sources contain terminal values.
 */
export function getEffectiveRefundStatus(
  order: Pick<OrderHistoryEntry, 'refund_status' | 'status'>
): RefundStatus | null {
  const refundStatus = normalize(order.refund_status);
  const generalStatus = normalize(order.status);
  if (isTerminalRefundStatus(refundStatus)) return refundStatus;
  if (isTerminalRefundStatus(generalStatus)) return generalStatus;
  return refundStatus || generalStatus;
}

export function getRefundUpdateTimestamp(
  order: Pick<
    OrderHistoryEntry,
    'refund_status' | 'status' | 'refund_updated_at' | 'status_updated_at' | 'cancelled_at' | 'updated_at'
  >
): string | undefined {
  const values = [
    order.refund_updated_at,
    order.status_updated_at,
    order.cancelled_at,
    // updated_at is often a tracking timestamp. Use it only as a legacy
    // fallback when the record actually contains a refund state.
    getEffectiveRefundStatus(order) ? order.updated_at : undefined,
  ].filter((value): value is string => Boolean(value));
  let newest: { value: string; timestamp: number } | null = null;
  for (const value of values) {
    const parsed = new Date(value).getTime();
    if (Number.isFinite(parsed) && (!newest || parsed > newest.timestamp)) {
      newest = { value, timestamp: parsed };
    }
  }
  return newest?.value;
}

export function shouldApplyLocalRefundOverride(
  apiOrder: OrderHistoryEntry,
  localOrder: OrderHistoryEntry
): boolean {
  const apiStatus = getEffectiveRefundStatus(apiOrder);
  const localStatus = getEffectiveRefundStatus(localOrder);
  if (isTerminalRefundStatus(apiStatus) && !isTerminalRefundStatus(localStatus)) return false;
  if (isTerminalRefundStatus(localStatus) && !isTerminalRefundStatus(apiStatus)) return true;

  const apiTime = new Date(getRefundUpdateTimestamp(apiOrder) || '').getTime();
  const localTime = new Date(getRefundUpdateTimestamp(localOrder) || '').getTime();
  if (!Number.isFinite(localTime)) return false;
  if (!Number.isFinite(apiTime)) return true;
  if (localTime !== apiTime) return localTime > apiTime;
  // Equal timestamps: the API has not caught up while it still reports a
  // different refund state, so keep the local override; once the states
  // converge the override is redundant and can be dropped.
  return apiStatus !== localStatus;
}
